import React from "react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { loginUser, fetchUserProfile } from "@/store/slices/authSlice";
import { User, Lock, Eye, EyeOff } from "lucide-react";
import { toast } from "react-toastify";
import { useAuth } from "@/hooks/useAuth"; // Using custom hook
import Lottie from "lottie-react";
import cyclingAnimation from "@/assets/icons/Cycling-json.json";
import ParticleBackground from "../home/ParticleBackground";
import Button from "../common/Button";
import TextInput from "../common/TextInput";

const LoginForm = () => {
  const [showPassword, setShowPassword] = React.useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, error, clearErrors } = useAuth(); // Using custom hook

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm();

  // Clear errors when component mounts
  React.useEffect(() => {
    clearErrors();
  }, []);

  const onSubmit = async (data) => {
    try {
      // 1. Attempt login
      await dispatch(loginUser(data)).unwrap();

      // 2. Fetch full profile + stats immediately after login
      await dispatch(fetchUserProfile());

      toast.success("Login successful!");

      // 3. Redirect to dashboard
      navigate("/profile");
    } catch (error) {
      toast.error(error.message || "Login failed");
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 overflow-hidden">
      {/* Dynamic Background */}
      <ParticleBackground />

      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 max-w-md w-full bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-8 transition-colors duration-300"
      >
        <div className="text-center mb-8">
          <div className="w-24 h-24  flex items-center justify-center mx-auto mb-4">
            <Lottie
              animationData={cyclingAnimation}
              loop={true}
              autoplay={true}
            />
          </div>
          <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100 transition-colors duration-300">
            Welcome Back
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-2 transition-colors duration-300">
            Sign in to your Koset account
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Email or Username */}
          <TextInput
            label="Email or Username"
            type="text"
            icon={<User className="w-5 h-5" />}
            placeholder="Enter your email or username"
            error={errors.identifier?.message}
            {...register("identifier", {
              required: "Email or username is required",
              minLength: {
                value: 3,
                message: "Must be at least 3 characters",
              },
            })}
          />

          {/* Password */}
          <div className="relative">
            <TextInput
              label="Password"
              type={showPassword ? "text" : "password"}
              icon={<Lock className="w-5 h-5" />}
              placeholder="Enter your password"
              error={errors.password?.message}
              {...register("password", {
                required: "Password is required",
                minLength: {
                  value: 6,
                  message: "Password must be at least 6 characters",
                },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-9 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-300"
            >
              {showPassword ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg"
            >
              {error}
            </motion.div>
          )}

          <Button
            type="submit"
            loading={isSubmitting}
            variant="primary"
            className="w-full py-3"
            size="lg"
          >
            Sign In
          </Button>
        </form>

        <div className="text-center mt-6">
          <p className="text-gray-600 dark:text-gray-400 transition-colors duration-300">
            Don't have an account?{" "}
            <Link
              to="/register"
              className="text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 font-semibold transition-colors duration-300"
            >
              Sign up
            </Link>
          </p>
        </div>

        {/* Business Portal Links */}
        <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
          <p className="text-center text-sm text-gray-500 dark:text-gray-400 mb-4">
            Business Portal
          </p>
          <p className="text-center text-xs text-gray-500 dark:text-gray-400 mb-3">
            Are you an Organizer or Sponsor?
          </p>
          <div className="flex gap-3">
            <Link
              to="/organizer/login"
              className="flex-1 py-2.5 text-center text-sm font-medium border border-indigo-200 dark:border-indigo-800 text-indigo-600 dark:text-indigo-400 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
            >
              Login as Organizer
            </Link>
            <Link
              to="/sponsor/login"
              className="flex-1 py-2.5 text-center text-sm font-medium border border-teal-200 dark:border-teal-800 text-teal-600 dark:text-teal-400 rounded-lg hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors"
            >
              Login as Sponsor
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginForm;
